import React from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chart from 'react-apexcharts';

function formatDate(value) {
  if (!value) return '';
  return String(value).substring(5, 10);
}

function formatNumber(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return `${(numberValue / 1000000).toFixed(1)}M`;
  }

  if (numberValue >= 1000) {
    return `${(numberValue / 1000).toFixed(1)}K`;
  }

  return numberValue.toFixed(0);
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function evolutionTickAmount(rowCount, density = 'desktop') {
  if (rowCount <= 1) return 1;
  if (rowCount <= 7) return rowCount - 1;
  if (density === 'narrow') return 3;
  if (density === 'compact') return 4;
  return rowCount <= 30 ? 9 : 11;
}

class PromotionsEvolution extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      metric: 'claims',
    };
  }

  handleMetricChange = (event, newMetric) => {
    if (newMetric !== null) {
      this.setState({ metric: newMetric });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const stringValue = String(value);
    const trimmedValue = stringValue.trimStart();
    const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
    const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue) ? `'${stringValue}` : stringValue;
    const cleanValue = safeValue.replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.props.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const meta = this.props.meta || {};
    const currency = meta.currency || 'RON';
    const promotionLabel = this.props.selectedPromotion?.label || 'All promotions';

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const totalIssuedClaimsConsumed = rows.reduce(
      (sum, row) => sum + Number(row.issued_claims_consumed_by_period_end || 0),
      0
    );

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const weightedUsageRate = totalClaimsCreated > 0
      ? (totalIssuedClaimsConsumed / totalClaimsCreated) * 100
      : 0;

    const totalGrossSalesFromClaims = totalSalesFromClaims + totalDiscountFromClaims;
    const weightedDiscountRate = totalGrossSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalGrossSalesFromClaims) * 100
      : 0;

    const averageDiscountPerPromoOrder = totalOrdersWithClaims > 0
      ? totalDiscountFromClaims / totalOrdersWithClaims
      : 0;

    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'claims_created', label: 'Claims issued' },
      { key: 'claims_consumed', label: 'Claims used' },
      { key: 'issued_claim_conversion', label: 'Issued conversion %' },
      { key: 'orders_with_claims', label: 'Promo orders' },
      { key: 'sales_from_claims', label: `Net promo sales (${currency})` },
      { key: 'discount_from_claims', label: `Promo discount (${currency})` },
      { key: 'discount_rate', label: 'Discount rate (% of gross promo sales)' },
      { key: 'average_discount', label: `Average discount/order (${currency})` },
    ];

    const csvRows = [
      ['Promotion activity evolution'].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Date from', meta.dateFrom || ''].map((value) => this.csvValue(value)).join(','),
      ['Date to', meta.dateTo || ''].map((value) => this.csvValue(value)).join(','),
      ['Timezone', meta.timezone || ''].map((value) => this.csvValue(value)).join(','),
      ['Currency', currency].map((value) => this.csvValue(value)).join(','),
      ['Promotion filter', promotionLabel].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total claims issued', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Issued claims consumed by period end', totalIssuedClaimsConsumed].map((value) => this.csvValue(value)).join(','),
      ['Weighted issued conversion %', `${weightedUsageRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Total promo orders', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total net promo sales', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total promo discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted discount rate (% of gross promo sales)', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per promo order', averageDiscountPerPromoOrder.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'issued_claim_conversion' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `promotion_activity_evolution_${meta.dateFrom || periodMode}_${meta.dateTo || ''}${meta.promotionIref ? `_promotion_${meta.promotionIref}` : ''}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.props.data || [];
    const meta = this.props.meta || {};
    const metric = this.state.metric;
    const periodMode = meta.periodMode || this.props.periodMode || 'last_30d';
    const singleDayView = periodMode === 'today' || periodMode === 'yesterday'
      || (Boolean(meta.dateFrom) && meta.dateFrom === meta.dateTo);
    const chartType = singleDayView ? 'bar' : 'line';
    const promotionOptions = this.props.promotionOptions || [
      { value: 'all', label: 'All promotions' },
    ];
    const selectedPromotion = this.props.selectedPromotion || promotionOptions[0];

    const categories = rows.map((row) => formatDate(row.date));

    let series = [
      {
        name: 'Claims issued',
        data: rows.map((row) => Number(row.claims_created || 0)),
      },
      {
        name: 'Claims used',
        data: rows.map((row) => Number(row.claims_consumed || 0)),
      },
    ];

    let yFormatter = formatNumber;
    let tooltipFormatter = formatNumber;

    if (metric === 'orders') {
      series = [
        {
          name: 'Promo orders',
          data: rows.map((row) => Number(row.orders_with_claims || 0)),
        },
        {
          name: 'Issued conversion',
          data: rows.map((row) => Number(
            row.issued_claim_conversion !== undefined
              ? row.issued_claim_conversion
              : row.claim_conversion || 0
          )),
        },
      ];

      tooltipFormatter = function (value, opts) {
        if (opts && opts.seriesIndex === 1) {
          return formatPercent(value);
        }

        return formatNumber(value);
      };
    }

    if (metric === 'sales') {
      series = [
        {
          name: 'Net promo sales',
          data: rows.map((row) => Number(row.sales_from_claims || 0)),
        },
        {
          name: 'Promo discount',
          data: rows.map((row) => Number(row.discount_from_claims || 0)),
        },
      ];
    }

    if (metric === 'cost') {
      series = [
        {
          name: 'Gross discount rate',
          data: rows.map((row) => Number(row.discount_rate || 0)),
        },
        {
          name: 'Avg. discount/order',
          data: rows.map((row) => Number(row.average_discount || 0)),
        },
      ];

      tooltipFormatter = function (value, opts) {
        if (opts && opts.seriesIndex === 0) {
          return formatPercent(value);
        }

        return formatNumber(value);
      };
    }

    let yaxis = {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: function (value) {
          return yFormatter(value);
        },
        style: {
          fontSize: '11px',
        },
      },
    };

    if (metric === 'orders') {
      yaxis = [
        {
          seriesName: 'Promo orders',
          min: 0,
          forceNiceScale: true,
          labels: { formatter: formatNumber },
        },
        {
          seriesName: 'Issued conversion',
          opposite: true,
          min: 0,
          max: 100,
          labels: { formatter: formatPercent },
        },
      ];
    }

    if (metric === 'cost') {
      yaxis = [
        {
          seriesName: 'Gross discount rate',
          min: 0,
          forceNiceScale: true,
          labels: { formatter: formatPercent },
        },
        {
          seriesName: 'Avg. discount/order',
          opposite: true,
          min: 0,
          forceNiceScale: true,
          labels: { formatter: formatNumber },
        },
      ];
    }

    const options = {
      chart: {
        type: chartType,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        parentHeightOffset: 0,
      },
      stroke: {
        curve: 'smooth',
        width: singleDayView ? 0 : 2.5,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '48%',
          borderRadius: 4,
        },
      },
      markers: {
        size: 0,
      },
      dataLabels: {
        enabled: singleDayView,
        formatter: function (value, opts) {
          return tooltipFormatter(value, opts);
        },
      },
      colors: ['#1a237e', '#607d8b'],
      grid: {
        borderColor: 'rgba(15, 23, 42, 0.08)',
        padding: {
          left: 8,
          right: 8,
          top: 0,
          bottom: 0,
        },
      },
      xaxis: {
        categories: categories,
        tickAmount: evolutionTickAmount(categories.length),
        tickPlacement: 'on',
        labels: {
          rotate: 0,
          rotateAlways: false,
          hideOverlappingLabels: false,
          showDuplicates: false,
          trim: false,
          maxHeight: 32,
          style: {
            fontSize: '11px',
          },
        },
      },
      yaxis: yaxis,
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (value, opts) {
            return tooltipFormatter(value, opts);
          },
        },
      },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        horizontalAlign: 'center',
        markers: {
          width: 9,
          height: 9,
        },
      },
      noData: {
        text: 'No promotion activity data available',
      },
      responsive: [
        {
          breakpoint: 700,
          options: {
            xaxis: {
              categories: categories,
              tickAmount: evolutionTickAmount(categories.length, 'compact'),
              tickPlacement: 'on',
              labels: {
                rotate: 0,
                rotateAlways: false,
                hideOverlappingLabels: false,
                showDuplicates: false,
                trim: false,
                maxHeight: 28,
                style: { fontSize: '10px' },
              },
            },
          },
        },
        {
          breakpoint: 420,
          options: {
            xaxis: {
              categories: categories,
              tickAmount: evolutionTickAmount(categories.length, 'narrow'),
              tickPlacement: 'on',
              labels: {
                rotate: 0,
                rotateAlways: false,
                hideOverlappingLabels: false,
                showDuplicates: false,
                trim: false,
                maxHeight: 26,
                style: { fontSize: '10px' },
              },
            },
          },
        },
      ],
    };

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    Promotion activity evolution
                  </Typography>
                  <Tooltip
                    title="Compare claims, promotion orders, sales impact and discount cost over the selected period."
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                  >
                    <IconButton
                      size="small"
                      aria-label="About promotion activity evolution"
                      sx={{ p: 0.25, color: '#64748b' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  maxWidth: '100%',
                  overflowX: { xs: 'auto', sm: 'visible' },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={!rows.length}
                  onClick={this.handleExportCsv}
                  sx={{
                    flexShrink: 0,
                    height: 32,
                    minWidth: 64,
                    px: 1,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.68rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    color: '#111827',
                    borderColor: 'rgba(15, 23, 42, 0.14)',
                    bgcolor: '#ffffff',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: 'rgba(15, 23, 42, 0.24)',
                    },
                    '& .MuiButton-startIcon': {
                      mr: 0.5,
                      ml: 0,
                    },
                  }}
                >
                  CSV
                </Button>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={metric}
                  onChange={this.handleMetricChange}
                  sx={{
                    flexShrink: 0,
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    maxWidth: '100%',
                    '& .MuiToggleButton-root': {
                      px: { xs: 0.85, sm: 1.25 },
                      py: 0.35,
                      fontSize: { xs: '0.68rem', sm: '0.78rem' },
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      color: '#334155',
                      borderColor: 'rgba(15, 23, 42, 0.08)',
                      '&.Mui-selected': {
                        bgcolor: '#0f172a',
                        color: '#fff',
                        '&:hover': {
                          bgcolor: '#111827',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="claims">Claims</ToggleButton>
                  <ToggleButton value="orders">Orders</ToggleButton>
                  <ToggleButton value="sales">Sales</ToggleButton>
                  <ToggleButton value="cost">Cost</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Autocomplete
              size="small"
              disableClearable
              autoHighlight
              loading={Boolean(this.props.promotionLoading)}
              disabled={Boolean(this.props.promotionDisabled)}
              options={promotionOptions}
              value={selectedPromotion}
              onChange={this.props.onPromotionChange}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => (
                String(option.value) === String(value.value)
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Promotion"
                  placeholder="Search promotion"
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Promotion activity filter',
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 300 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
              }}
            />

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <Chart
                key={chartType}
                options={options}
                series={series}
                type={chartType}
                height="100%"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsEvolution;
